import React, { createContext, useContext, useEffect, useState } from 'react';
import { getConnectionQuality } from '@/utils/performanceOptimizations';

interface MobilePerformanceContextType {
  connectionQuality: 'slow' | 'medium' | 'fast';
  shouldReduceAnimations: boolean;
  shouldLazyLoad: boolean;
  prefersReducedMotion: boolean;
}

const MobilePerformanceContext = createContext<MobilePerformanceContextType>({
  connectionQuality: 'medium',
  shouldReduceAnimations: false,
  shouldLazyLoad: true,
  prefersReducedMotion: false
});

export const useMobilePerformance = () => useContext(MobilePerformanceContext);

export const MobilePerformanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [connectionQuality, setConnectionQuality] = useState<'slow' | 'medium' | 'fast'>('medium');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Détecter la qualité de connexion
    setConnectionQuality(getConnectionQuality());

    // Détecter si l'utilisateur préfère réduire les animations
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const shouldReduceAnimations = connectionQuality === 'slow' || prefersReducedMotion;
  const shouldLazyLoad = connectionQuality !== 'slow';

  const value: MobilePerformanceContextType = {
    connectionQuality,
    shouldReduceAnimations,
    shouldLazyLoad,
    prefersReducedMotion
  };

  return (
    <MobilePerformanceContext.Provider value={value}>
      {children}
    </MobilePerformanceContext.Provider>
  );
};